import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AlertTriangle, Info, Loader2 } from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogFooter, DialogTitle, DialogDescription,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';

/**
 * WO-44 — the screen before Stripe's payment page: billing country + "company or
 * individual", and the company's name, VAT number and address. The country decides
 * the route (backend/services/sales_rules.py is the rule; this screen mirrors it):
 *   EU, open (QA, KW, US, HK)      anyone
 *   Managed Payments countries      companies; individuals only when Stripe Managed
 *                                   Payments is switched on (plans and catalogue reports;
 *                                   never white-label licences — WO-67)
 *   every other country             VAT-registered companies only — warning shown,
 *                                   company fields mandatory
 * The server checks everything again; this screen only avoids a refusal after typing.
 */

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const GCC = ['SA', 'AE', 'QA', 'KW', 'BH', 'OM'];
const ISO = ('AD AE AF AG AI AL AM AO AR AS AT AU AW AX AZ BA BB BD BE BF BG BH BI BJ BL BM BN BO BQ BR BS BT BW BY BZ '
  + 'CA CC CD CF CG CH CI CK CL CM CN CO CR CU CV CW CX CY CZ DE DJ DK DM DO DZ EC EE EG EH ER ES ET FI FJ FK FM FO FR '
  + 'GA GB GD GE GF GG GH GI GL GM GN GP GQ GR GT GU GW GY HK HN HR HT HU ID IE IL IM IN IQ IR IS IT JE JM JO JP KE KG '
  + 'KH KI KM KN KP KR KW KY KZ LA LB LC LI LK LR LS LT LU LV LY MA MC MD ME MF MG MH MK ML MM MN MO MP MQ MR MS MT MU '
  + 'MV MW MX MY MZ NA NC NE NF NG NI NL NO NP NR NU NZ OM PA PE PF PG PH PK PL PM PN PR PS PT PW PY QA RE RO RS RU RW '
  + 'SA SB SC SD SE SG SH SI SK SL SM SN SO SR SS ST SV SX SY SZ TC TD TG TH TJ TK TL TM TN TO TR TT TV TW TZ UA UG US '
  + 'UY UZ VA VC VE VG VI VN VU WF WS XK YE YT ZA ZM ZW').split(' ');

// Same formats the server enforces for the Gulf (sales_rules._STRICT).
const STRICT = {
  SA: { re: /^3\d{13}3$/, en: '15 digits, starting and ending with 3', ar: '15 رقمًا، يبدأ بالرقم 3 وينتهي به' },
  AE: { re: /^\d{15}$/, en: '15 digits', ar: '15 رقمًا' },
  BH: { re: /^\d{15}$/, en: '15 digits', ar: '15 رقمًا' },
  OM: { re: /^OM\d{10}$/, en: 'OM followed by 10 digits', ar: 'OM متبوعًا بعشرة أرقام' },
};
const STORE_KEY = 'm2b_billing_details';

function loadSaved() {
  try { return JSON.parse(localStorage.getItem(STORE_KEY) || 'null') || {}; } catch { return {}; }
}
function save(d) {
  try { localStorage.setItem(STORE_KEY, JSON.stringify(d)); } catch { /* storage unavailable */ }
}

function groupFor(rules, c) {
  if (!rules || !c) return null;
  if (rules.blocked.includes(c)) return 'blocked';
  if (rules.eu.includes(c)) return 'eu';
  if (rules.open.includes(c)) return 'open';
  if (rules.mp.includes(c)) return 'mp';
  return 'company_only';
}

export function BillingDetailsDialog({ open, kind, onDone }) {
  const { language } = useLanguage();
  const { user, token } = useAuth();
  const t = (en, ar) => (language === 'ar' ? ar : en);

  const [rules, setRules] = useState(null);
  const [rulesError, setRulesError] = useState(false);
  const [country, setCountry] = useState('');
  const [buyer, setBuyer] = useState('');
  const [f, setF] = useState({ company_name: '', vat_number: '', address_line1: '', city: '', postal_code: '' });
  const [notified, setNotified] = useState(false);
  const [notifying, setNotifying] = useState(false);
  // WO-71 (BILAL 20/10/2026 §3) — a referred buyer is told the code's owner earns a
  // commission; a buyer from the referrer's own organisation confirms it knows.
  const [refStatus, setRefStatus] = useState(null);
  const [orgKnows, setOrgKnows] = useState(false);

  // Reset from the last details this browser used, every time the screen opens.
  useEffect(() => {
    if (!open) return;
    const s = loadSaved();
    setCountry(s.country || '');
    setBuyer(s.buyer_type || '');
    setF({ company_name: s.company_name || '', vat_number: s.vat_number || '', address_line1: s.address_line1 || '',
      city: s.city || '', postal_code: s.postal_code || '' });
    setNotified(false);
    setRulesError(false);
    setRefStatus(null);
    setOrgKnows(false);
    if (token) {
      fetch(`${BACKEND_URL}/api/referral/my-referral-status`, { headers: { Authorization: `Bearer ${token}` } })
        .then((r) => (r.ok ? r.json() : null))
        .then((d) => setRefStatus(d))  // the box is never pre-ticked (BILAL 23/10/2026 §5.4)
        .catch(() => setRefStatus(null));
    }
    fetch(`${BACKEND_URL}/api/payments/sales-rules`)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))
      .then(setRules)
      .catch(() => { setRules(null); setRulesError(true); });
  }, [open, token]);

  const names = useMemo(() => {
    let dn = null;
    try { dn = new Intl.DisplayNames([language === 'ar' ? 'ar' : 'en'], { type: 'region' }); } catch { dn = null; }
    const name = (c) => (dn ? dn.of(c) : c) || c;
    const rest = ISO.filter((c) => !GCC.includes(c)).map((c) => [c, name(c)])
      .sort((a, b) => a[1].localeCompare(b[1], language === 'ar' ? 'ar' : 'en'));
    return { gcc: GCC.map((c) => [c, name(c)]), rest, of: name };
  }, [language]);

  const group = groupFor(rules, country);
  const individualAllowed = group === 'eu' || group === 'open'
    || (group === 'mp' && (kind === 'plan' || kind === 'report') && !!rules?.managed_payments);  // WO-67
  const companyOnly = !!group && group !== 'blocked' && !individualAllowed;
  const effectiveBuyer = companyOnly ? 'company' : buyer;
  const countryName = country ? names.of(country) : '';

  // A refusal shown on screen is counted per country (WARREN's demand measure).
  useEffect(() => {
    if (!open || !companyOnly || !country || !token) return;
    fetch(`${BACKEND_URL}/api/payments/paid-plan-interest`, {
      method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ country, kind: 'refused' }),
    }).catch(() => { /* counting never blocks the buyer */ });
  }, [open, companyOnly, country, token]);

  const vatClean = f.vat_number.replace(/[\s.-]/g, '').toUpperCase();
  const strict = STRICT[country];
  const vatBad = effectiveBuyer === 'company' && !!f.vat_number && !!strict && !strict.re.test(vatClean);
  const companyComplete = f.company_name.trim() && f.vat_number.trim() && f.address_line1.trim() && f.city.trim() && !vatBad;
  const canContinue = !!rules && !!group && group !== 'blocked' && !!effectiveBuyer
    && (effectiveBuyer === 'individual' || companyComplete);

  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  // WO-71 — the own-organisation box: buying as a company (or a white-label licence)
  // with the code of someone at the same organisation (Referral Terms 5.4).
  const askOrg = !!(refStatus && refStatus.referred && refStatus.same_organisation
    && (effectiveBuyer === 'company' || kind === 'whitelabel'));

  const handleNotify = async () => {
    setNotifying(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/payments/paid-plan-interest`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ country, kind: 'notify' }),
      });
      if (!r.ok) throw new Error(String(r.status));
      setNotified(true);
    } catch {
      toast.error(t('Could not save your request. Please try again.', 'تعذّر حفظ طلبك. يرجى المحاولة مرة أخرى.'));
    } finally {
      setNotifying(false);
    }
  };

  const handleContinue = () => {
    const details = effectiveBuyer === 'company'
      ? { country, buyer_type: 'company', company_name: f.company_name.trim(), vat_number: f.vat_number.trim(),
          address_line1: f.address_line1.trim(), city: f.city.trim(), postal_code: f.postal_code.trim() || null }
      : { country, buyer_type: 'individual' };
    save({ ...f, country, buyer_type: effectiveBuyer });
    if (askOrg) {
      details.referral_disclosure_ack = !!orgKnows;  // WO-71
    }
    onDone(details);
  };

  const radio = (value, label, disabled) => (
    <label className={`flex items-center gap-2 rounded-md border p-3 text-sm ${disabled ? 'opacity-50' : 'cursor-pointer'} ${effectiveBuyer === value ? 'border-primary bg-primary/5' : 'border-border'}`}>
      <input type="radio" name="m2b-buyer" value={value} checked={effectiveBuyer === value} disabled={disabled}
        onChange={() => setBuyer(value)} data-testid={`billing-buyer-${value}`} />
      <span>{label}</span>
    </label>
  );

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onDone(null); }}>
      <DialogContent className="sm:max-w-lg max-h-[calc(100dvh-2rem)] overflow-y-auto" data-testid="billing-details-dialog"
        dir={language === 'ar' ? 'rtl' : 'ltr'}>
        <DialogHeader>
          <DialogTitle>{t('Billing details', 'بيانات الفوترة')}</DialogTitle>
          <DialogDescription>
            {t('Tell us where you are billed from. This decides the tax on your invoice.',
              'أخبرنا بالبلد الذي تصدر منه الفاتورة، فهو يحدد الضريبة على فاتورتك.')}
          </DialogDescription>
        </DialogHeader>

        {rulesError && (
          <p className="text-sm text-destructive" data-testid="billing-rules-error">
            {t('Could not load the billing rules. Nothing has been charged — please try again.',
              'تعذّر تحميل قواعد الفوترة. لم يتم خصم أي مبلغ — يرجى المحاولة مرة أخرى.')}
          </p>
        )}

        <div className="space-y-4">
          <div className="space-y-1">
            <label className="text-sm font-medium" htmlFor="m2b-billing-country">{t('Billing country', 'بلد الفوترة')}</label>
            <select id="m2b-billing-country" data-testid="billing-country" value={country}
              onChange={(e) => { setCountry(e.target.value); setNotified(false); }}
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm">
              <option value="">{t('Select your country', 'اختر بلدك')}</option>
              <optgroup label={t('Gulf countries', 'دول الخليج')}>
                {names.gcc.map(([c, n]) => <option key={c} value={c}>{n}</option>)}
              </optgroup>
              <optgroup label={t('All countries', 'جميع الدول')}>
                {names.rest.map(([c, n]) => <option key={c} value={c}>{n}</option>)}
              </optgroup>
            </select>
          </div>

          {group === 'blocked' && (
            <p className="text-sm text-destructive" data-testid="billing-blocked">
              {t('Paid plans and reports are not available in your country.', 'الخطط المدفوعة والتقارير غير متاحة في بلدك.')}
            </p>
          )}

          {group && group !== 'blocked' && (
            <>
              {companyOnly && (
                <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:bg-amber-950/40 dark:text-amber-200 space-y-2" data-testid="billing-company-only-warning">
                  <p className="flex gap-2 font-semibold">
                    <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
                    {(kind === 'report' || kind === 'whitelabel')
                      ? t(`Reports in ${countryName} are available to VAT-registered companies only.`,
                        `التقارير في ${countryName} متاحة للشركات المسجّلة في ضريبة القيمة المضافة فقط.`)
                      : t(`Paid plans in ${countryName} are available to VAT-registered companies only.`,
                        `الخطط المدفوعة في ${countryName} متاحة للشركات المسجّلة في ضريبة القيمة المضافة فقط.`)}
                  </p>
                  <p>
                    {t("To continue, enter your company's name, VAT registration number and address. They will appear on your invoice. If your company is not VAT-registered, you can keep using Mid2Bid Free.",
                      'للمتابعة، أدخل اسم شركتك ورقم تسجيلها الضريبي وعنوانها، وستظهر هذه البيانات على فاتورتك. إذا لم تكن شركتك مسجّلة في ضريبة القيمة المضافة، يمكنك الاستمرار في استخدام Mid2Bid المجاني.')}
                  </p>
                  {kind === 'plan' && (notified ? (
                    <p data-testid="billing-notify-done">
                      {t(`Done — we'll email you at ${user?.email || 'your account e-mail'}.`,
                        `تم — سنراسلك على ${user?.email || 'بريد حسابك'}.`)}
                    </p>
                  ) : (
                    <Button type="button" variant="outline" size="sm" onClick={handleNotify} disabled={notifying} data-testid="billing-notify">
                      {notifying && <Loader2 className="h-4 w-4 animate-spin" />}
                      {t(`Tell me when paid plans open in ${countryName}`, `أبلغني عند إتاحة الخطط المدفوعة في ${countryName}`)}
                    </Button>
                  ))}
                </div>
              )}

              <div className="space-y-2">
                <p className="text-sm font-medium">{t('You are buying as', 'تشتري بصفتك')}</p>
                {radio('company', t('A VAT-registered company', 'شركة مسجّلة في ضريبة القيمة المضافة'), false)}
                {radio('individual', t('An individual, or a company without a VAT number', 'فرد، أو شركة ليس لديها رقم ضريبي'), companyOnly)}
              </div>

              {effectiveBuyer === 'company' && (
                <div className="space-y-3" data-testid="billing-company-fields">
                  <Input placeholder={t('Company name *', 'اسم الشركة *')} value={f.company_name} onChange={set('company_name')} data-testid="billing-company-name" />
                  <div className="space-y-1">
                    <Input placeholder={t('VAT registration number *', 'الرقم الضريبي (ضريبة القيمة المضافة) *')} value={f.vat_number} onChange={set('vat_number')} data-testid="billing-vat" />
                    {strict && (
                      <p className={`text-xs ${vatBad ? 'text-destructive' : 'text-muted-foreground'}`} data-testid="billing-vat-hint">
                        {vatBad
                          ? t(`This is not a valid VAT number for ${countryName} (${strict.en}).`, `هذا ليس رقمًا ضريبيًا صالحًا في ${countryName} (${strict.ar}).`)
                          : t(strict.en, strict.ar)}
                      </p>
                    )}
                  </div>
                  <Input placeholder={t('Address *', 'العنوان *')} value={f.address_line1} onChange={set('address_line1')} data-testid="billing-address" />
                  <div className="grid grid-cols-2 gap-3">
                    <Input placeholder={t('City *', 'المدينة *')} value={f.city} onChange={set('city')} data-testid="billing-city" />
                    <Input placeholder={t('Postal code (optional)', 'الرمز البريدي (اختياري)')} value={f.postal_code} onChange={set('postal_code')} data-testid="billing-postal" />
                  </div>
                  {group !== 'eu' && (
                    <p className="flex gap-2 text-xs text-muted-foreground">
                      <Info className="h-4 w-4 shrink-0" />
                      {t('No VAT is added: your company accounts for any VAT in its own country (reverse charge).',
                        'لن تُضاف ضريبة القيمة المضافة: تتولى شركتك احتساب أي ضريبة مستحقة في بلدها (آلية الاحتساب العكسي).')}
                    </p>
                  )}
                </div>
              )}

              {effectiveBuyer === 'individual' && group === 'mp' && (
                <p className="flex gap-2 text-xs text-muted-foreground" data-testid="billing-mp-note">
                  <Info className="h-4 w-4 shrink-0" />
                  {t("Your payment is processed by Stripe's company Link as the seller. Local VAT is added at checkout, and the price may be shown in your local currency.",
                    'تُعالَج دفعتك عبر شركة Link التابعة لـ Stripe بصفتها البائع. تُضاف ضريبة القيمة المضافة المحلية عند الدفع، وقد يظهر السعر بعملتك المحلية.')}
                </p>
              )}
            </>
          )}

          {refStatus && refStatus.referred && (
            <div className="rounded-md border border-emerald-300 bg-emerald-50 dark:bg-emerald-950/30 dark:border-emerald-800 p-3 text-xs space-y-2" data-testid="billing-referral-disclosure">
              <p className="flex gap-2">
                <Info className="h-4 w-4 shrink-0" />
                <span>
                  {t('The person whose code you used receives a commission from Mid2Bid on your payments. This does not change your price.',
                    'يحصل صاحب الرمز الذي استخدمته على عمولة من Mid2Bid على مدفوعاتك. ولا يغيّر ذلك السعر الذي تدفعه.')}
                  {refStatus.discount_percent > 0 && t(` Your ${refStatus.discount_percent}% referral discount is applied.`,
                    ` يُطبَّق خصم الإحالة البالغ ${refStatus.discount_percent}٪.`)}
                </span>
              </p>
              {askOrg && (
                <label className="flex items-start gap-2 cursor-pointer" data-testid="billing-referral-same-org">
                  <input type="checkbox" className="mt-0.5" checked={orgKnows} onChange={(e) => setOrgKnows(e.target.checked)}
                    data-testid="billing-referral-same-org-ack" />
                  <span dir="ltr">
                    {`Our organisation knows that ${refStatus.code_owner_masked || 'the colleague'}, who gave us this code, receives a commission from Mid2Bid on this purchase.`}
                  </span>
                </label>
              )}
            </div>
          )}

          <p className="text-xs text-muted-foreground">
            {t('By continuing you accept the ', 'بالمتابعة، فإنك توافق على ')}
            <a href="/terms-of-service" target="_blank" rel="noopener noreferrer" className="underline">{t('Terms of Service', 'شروط الخدمة')}</a>.
          </p>
        </div>

        <DialogFooter className="gap-2">
          <Button type="button" variant="outline" onClick={() => onDone(null)} data-testid="billing-cancel">{t('Cancel', 'إلغاء')}</Button>
          <Button type="button" onClick={handleContinue} disabled={!canContinue} data-testid="billing-continue">
            {t('Continue to payment', 'المتابعة إلى الدفع')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** askBillingDetails(kind) opens the screen and resolves with the details, or null
 *  when the buyer cancels. Render {billingDialog} once in the page. */
export function useBillingDetails() {
  const [pending, setPending] = useState(null);
  const resolver = useRef(null);
  const askBillingDetails = useCallback((kind) => new Promise((resolve) => {
    resolver.current = resolve;
    setPending({ kind });
  }), []);
  const onDone = useCallback((value) => {
    const r = resolver.current;
    resolver.current = null;
    setPending(null);
    if (r) r(value);
  }, []);
  const billingDialog = <BillingDetailsDialog open={!!pending} kind={pending?.kind || 'plan'} onDone={onDone} />;
  return { billingDialog, askBillingDetails };
}

export default BillingDetailsDialog;
